import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import Button from "../components/ui/button/Button";
import AddPropertyManagerDialog from "../components/property-managers/AddPropertyManagerDialog";
import {
  BuildingManager,
  buildingManagerService,
  PropertyManagerApiError,
  PropertyManagerMutationResult,
} from "../services/buildingManagerService";
import { useBuilding } from "../context/BuildingContext";

function initials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "PM";
}

type StatusFeedback = {
  tone: "success" | "warning";
  message: string;
};

function completionFeedback(
  result: PropertyManagerMutationResult,
  buildingName: string,
): StatusFeedback {
  if (result.outcome === "created_and_attached") {
    if (result.setup_email?.status === "sent") {
      return {
        tone: "success",
        message: `${result.manager.name} was created and a setup link was sent to ${result.manager.email}.`,
      };
    }

    if (result.setup_email?.status === "failed") {
      return {
        tone: "warning",
        message: `${result.manager.name} was created and added to ${buildingName}, but the setup email could not be sent. Use Send setup link to try again.`,
      };
    }

    return {
      tone: "success",
      message: `${result.manager.name} was created and added to ${buildingName}.`,
    };
  }
  if (result.outcome === "reactivated") {
    return {
      tone: "success",
      message: `${result.manager.name}’s access to ${buildingName} was restored.`,
    };
  }
  if (result.outcome === "already_attached") {
    return {
      tone: "success",
      message: `${result.manager.name} is already assigned to ${buildingName}.`,
    };
  }
  return {
    tone: "success",
    message: `${result.manager.name} was added to ${buildingName}.`,
  };
}

export default function PropertyManagers() {
  const { id } = useParams<{ id: string }>();
  const buildingId = Number(id);
  const { currentBuilding } = useBuilding();
  const queryClient = useQueryClient();
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const [filter, setFilter] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [managerToRemove, setManagerToRemove] = useState<BuildingManager | null>(null);
  const [managerToSendSetupLink, setManagerToSendSetupLink] = useState<BuildingManager | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isSendingSetupLink, setIsSendingSetupLink] = useState(false);
  const [setupLinkError, setSetupLinkError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [statusFeedback, setStatusFeedback] = useState<StatusFeedback | null>(null);

  const managersQuery = useQuery({
    queryKey: ["building-property-managers", buildingId],
    queryFn: ({ signal }) => buildingManagerService.listForBuilding(buildingId, signal),
    enabled: Number.isInteger(buildingId) && buildingId > 0,
    staleTime: 30_000,
  });

  const managers = useMemo(() => managersQuery.data?.data ?? [], [managersQuery.data?.data]);
  const normalizedFilter = filter.trim().toLowerCase();
  const filteredManagers = useMemo(
    () =>
      managers.filter(
        (manager) =>
          !normalizedFilter ||
          manager.name.toLowerCase().includes(normalizedFilter) ||
          manager.email.toLowerCase().includes(normalizedFilter),
      ),
    [managers, normalizedFilter],
  );
  const buildingName = currentBuilding?.name || "this building";

  const completed = async (result: PropertyManagerMutationResult) => {
    setIsAddOpen(false);
    setStatusFeedback(completionFeedback(result, buildingName));
    await queryClient.invalidateQueries({ queryKey: ["building-property-managers", buildingId] });
    window.setTimeout(() => addButtonRef.current?.focus(), 0);
  };

  const sendSetupLink = async () => {
    if (!managerToSendSetupLink) return;

    setIsSendingSetupLink(true);
    setSetupLinkError(null);
    try {
      await buildingManagerService.sendSetupLink(buildingId, managerToSendSetupLink.id);
      setStatusFeedback({
        tone: "success",
        message: `A setup link was sent to ${managerToSendSetupLink.email}.`,
      });
      setManagerToSendSetupLink(null);
    } catch (error) {
      if (error instanceof PropertyManagerApiError && error.status === 429) {
        setSetupLinkError("A setup link was sent recently. Wait 60 seconds before trying again.");
      } else if (error instanceof PropertyManagerApiError && error.status === 503) {
        setSetupLinkError("The email service is unavailable right now. The account and building access are unchanged. Try again later.");
      } else if (error instanceof PropertyManagerApiError && error.status === 409) {
        setSetupLinkError("This account is disabled. Enable it before sending a setup link.");
      } else if (error instanceof PropertyManagerApiError && error.status === 404) {
        setSetupLinkError("This property manager is no longer active for this building.");
      } else {
        setSetupLinkError(error instanceof Error ? error.message : "Failed to send setup link.");
      }
    } finally {
      setIsSendingSetupLink(false);
    }
  };

  const remove = async () => {
    if (!managerToRemove) return;
    setIsRemoving(true);
    setMutationError(null);
    try {
      await buildingManagerService.removeFromBuilding(buildingId, managerToRemove.id);
      setStatusFeedback({
        tone: "success",
        message: `${managerToRemove.name} was removed from ${buildingName}.`,
      });
      setManagerToRemove(null);
      await queryClient.invalidateQueries({ queryKey: ["building-property-managers", buildingId] });
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : "Failed to remove property manager.");
    } finally {
      setIsRemoving(false);
    }
  };

  if (!Number.isInteger(buildingId) || buildingId <= 0) {
    return <div className="py-20 text-center text-gray-500">Select a building to manage property managers.</div>;
  }

  return (
    <>
      <PageMeta title="Property Managers | SBM Package Admin" description="Manage property managers assigned to this building" />
      <div aria-live="polite" className="sr-only">{statusFeedback?.message}</div>

      {statusFeedback && (
        <div
          className={`fixed right-6 top-6 z-99999 max-w-md rounded-lg px-5 py-3 text-sm font-medium text-white shadow-lg ${statusFeedback.tone === "warning" ? "bg-amber-600" : "bg-green-600"}`}
          role="status"
        >
          {statusFeedback.message}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">Property Managers</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{currentBuilding?.name}</p>
          </div>
          <Button
            ref={addButtonRef}
            size="sm"
            onClick={() => {
              setMutationError(null);
              setIsAddOpen(true);
            }}
            className="w-full sm:w-auto"
          >
            Add property manager
          </Button>
        </div>

        {mutationError && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{mutationError}</div>}

        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex flex-col gap-4 border-b border-gray-200 p-5 dark:border-gray-800 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Assigned property managers</h2>
              <p className="mt-1 text-xs text-gray-500">{managersQuery.data?.pagination.total ?? 0} assigned</p>
            </div>
            <div>
              <label htmlFor="assigned-property-manager-filter" className="sr-only">Filter assigned property managers</label>
              <div className="relative">
                <svg aria-hidden="true" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input id="assigned-property-manager-filter" type="search" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filter assigned managers" className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-64" />
              </div>
            </div>
          </div>

          {managersQuery.isLoading && (
            <div aria-label="Loading property managers" className="divide-y divide-gray-100 dark:divide-gray-800">
              {[0, 1, 2].map((item) => <div key={item} className="flex animate-pulse items-center gap-4 p-5"><div className="h-11 w-11 rounded-full bg-gray-200 dark:bg-gray-800" /><div className="flex-1"><div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-800" /><div className="mt-2 h-3 w-56 rounded bg-gray-100 dark:bg-gray-800/70" /></div></div>)}
            </div>
          )}

          {managersQuery.isError && (
            <div className="p-10 text-center" role="alert">
              <p className="text-sm text-red-600">{managersQuery.error instanceof Error ? managersQuery.error.message : "Failed to load property managers."}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => managersQuery.refetch()}>Try again</Button>
            </div>
          )}

          {managersQuery.isSuccess && filteredManagers.length > 0 && (
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {filteredManagers.map((manager) => (
                <div key={manager.id} className="flex flex-col gap-4 p-4 hover:bg-gray-50 dark:hover:bg-white/[0.02] sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-100 font-medium text-brand-600 dark:bg-brand-500/20 dark:text-brand-400">
                      {manager.image_url ? <img src={manager.image_url} alt="" className="h-full w-full object-cover" /> : initials(manager.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-gray-800 dark:text-white/90">{manager.name}</p>
                      <p className="truncate text-sm text-gray-500 dark:text-gray-400">{manager.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${manager.account_status === "active" ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400" : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>{manager.account_status === "active" ? "Active" : "Disabled"}</span>
                    {manager.account_status === "active" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSetupLinkError(null);
                          setManagerToSendSetupLink(manager);
                        }}
                      >
                        Send setup link
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => { setMutationError(null); setManagerToRemove(manager); }}>Remove</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {managersQuery.isSuccess && filteredManagers.length === 0 && (
            <div className="px-6 py-14 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800"><span aria-hidden="true" className="text-xl">👤</span></div>
              <p className="mt-4 font-medium text-gray-700 dark:text-gray-300">{normalizedFilter ? `No assigned property managers match “${filter.trim()}”.` : "No property managers are assigned to this building."}</p>
              {!normalizedFilter && <Button size="sm" className="mt-5" onClick={() => setIsAddOpen(true)}>Add property manager</Button>}
            </div>
          )}
        </div>
      </div>

      <AddPropertyManagerDialog buildingId={buildingId} buildingName={buildingName} isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onCompleted={completed} />

      {managerToSendSetupLink && (
        <div className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50 p-4">
          <div role="alertdialog" aria-modal="true" aria-labelledby="send-setup-link-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-theme-xl dark:bg-gray-900">
            <h2 id="send-setup-link-title" className="text-lg font-semibold text-gray-800 dark:text-white/90">Send setup link?</h2>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-400">
              Send a one-time password setup link to <span className="font-medium text-gray-800 dark:text-gray-200">{managerToSendSetupLink.email}</span>? This will not change their current password or building access.
            </p>
            {setupLinkError && <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{setupLinkError}</p>}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" autoFocus disabled={isSendingSetupLink} onClick={() => { setSetupLinkError(null); setManagerToSendSetupLink(null); }}>Cancel</Button>
              <Button disabled={isSendingSetupLink} onClick={sendSetupLink}>{isSendingSetupLink ? "Sending…" : "Send setup link"}</Button>
            </div>
          </div>
        </div>
      )}

      {managerToRemove && (
        <div className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50 p-4">
          <div role="alertdialog" aria-modal="true" aria-labelledby="remove-property-manager-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-theme-xl dark:bg-gray-900">
            <h2 id="remove-property-manager-title" className="text-lg font-semibold text-gray-800 dark:text-white/90">Remove from this building?</h2>
            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-400">Remove {managerToRemove.name} from {buildingName}? They will keep access to any other assigned buildings.</p>
            {mutationError && <p role="alert" className="mt-4 text-sm text-red-600">{mutationError}</p>}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" autoFocus disabled={isRemoving} onClick={() => setManagerToRemove(null)}>Cancel</Button>
              <Button disabled={isRemoving} onClick={remove}>{isRemoving ? "Removing…" : "Remove access"}</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
